import type {
  DataEndpointRateLimit,
  DataEndpointRateLimitInput,
} from "./api";

/** Client-side bounds (must match server Min/MaxEndpointRPM). */
export const ENDPOINT_RPM_MIN = 1;
export const ENDPOINT_RPM_MAX = 10000;

/** Gateway defaults when the API omits default_* (must match server). */
export const FALLBACK_DEFAULT_PUBLIC_RPM = 120;
export const FALLBACK_DEFAULT_SERVICE_RPM = 600;

/**
 * Normalize a control-plane rate-limit payload so the UI always has
 * effective/default/override fields even if an older API omitted them.
 */
export function normalizeDataEndpointRateLimit(
  raw: Partial<DataEndpointRateLimit> | null | undefined,
  fallbackTable = "",
): DataEndpointRateLimit {
  const defaultPublic =
    typeof raw?.default_public_rpm === "number"
      ? raw.default_public_rpm
      : FALLBACK_DEFAULT_PUBLIC_RPM;
  const defaultService =
    typeof raw?.default_service_rpm === "number"
      ? raw.default_service_rpm
      : FALLBACK_DEFAULT_SERVICE_RPM;

  const publicRpm =
    raw?.public_rpm === undefined || raw?.public_rpm === null
      ? null
      : Number(raw.public_rpm);
  const serviceRpm =
    raw?.service_rpm === undefined || raw?.service_rpm === null
      ? null
      : Number(raw.service_rpm);

  const publicOverridden =
    typeof raw?.public_overridden === "boolean"
      ? raw.public_overridden
      : publicRpm != null && !Number.isNaN(publicRpm);
  const serviceOverridden =
    typeof raw?.service_overridden === "boolean"
      ? raw.service_overridden
      : serviceRpm != null && !Number.isNaN(serviceRpm);

  const effectivePublic =
    typeof raw?.effective_public_rpm === "number"
      ? raw.effective_public_rpm
      : publicOverridden && publicRpm != null
        ? publicRpm
        : defaultPublic;
  const effectiveService =
    typeof raw?.effective_service_rpm === "number"
      ? raw.effective_service_rpm
      : serviceOverridden && serviceRpm != null
        ? serviceRpm
        : defaultService;

  const usingDefaults =
    typeof raw?.using_defaults === "boolean"
      ? raw.using_defaults
      : !publicOverridden && !serviceOverridden;

  return {
    project_id: raw?.project_id ?? "",
    table_name: raw?.table_name ?? fallbackTable,
    public_rpm: publicOverridden ? publicRpm : null,
    service_rpm: serviceOverridden ? serviceRpm : null,
    public_overridden: publicOverridden,
    service_overridden: serviceOverridden,
    using_defaults: usingDefaults,
    effective_public_rpm: effectivePublic,
    effective_service_rpm: effectiveService,
    default_public_rpm: defaultPublic,
    default_service_rpm: defaultService,
    created_at: raw?.created_at,
    updated_at: raw?.updated_at,
  };
}

export function formatEffectiveRateLimitMessage(
  item: DataEndpointRateLimit,
): string {
  if (item.using_defaults) {
    return "Saved — using global gateway defaults.";
  }
  return `Saved. Effective: public ${item.effective_public_rpm}/min · service ${item.effective_service_rpm}/min.`;
}

export type RpmFieldState = {
  /** Empty string means "leave on default / do not send override". */
  value: string;
  error: string | null;
};

export function parseOptionalRpm(
  raw: string,
  fieldLabel: string,
): { ok: true; value: number | null } | { ok: false; error: string } {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return { ok: true, value: null };
  }
  if (!/^\d+$/.test(trimmed)) {
    return {
      ok: false,
      error: `${fieldLabel} must be a whole number ${ENDPOINT_RPM_MIN}–${ENDPOINT_RPM_MAX}`,
    };
  }
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < ENDPOINT_RPM_MIN || n > ENDPOINT_RPM_MAX) {
    return {
      ok: false,
      error: `${fieldLabel} must be between ${ENDPOINT_RPM_MIN} and ${ENDPOINT_RPM_MAX}`,
    };
  }
  return { ok: true, value: n };
}

/**
 * Build a partial PUT body from form fields relative to the loaded settings.
 * Only includes fields the user changed vs the loaded snapshot so we do not
 * accidentally clear the other key type.
 */
export function buildRateLimitPatch(
  publicRaw: string,
  serviceRaw: string,
  loaded: DataEndpointRateLimit | null,
):
  | { ok: true; body: DataEndpointRateLimitInput; noChange: boolean }
  | { ok: false; error: string } {
  const pub = parseOptionalRpm(publicRaw, "Public key RPM");
  if (!pub.ok) return pub;
  const svc = parseOptionalRpm(serviceRaw, "Service key RPM");
  if (!svc.ok) return svc;

  const prevPub =
    loaded?.public_rpm != null && loaded.public_overridden
      ? loaded.public_rpm
      : null;
  const prevSvc =
    loaded?.service_rpm != null && loaded.service_overridden
      ? loaded.service_rpm
      : null;

  const body: DataEndpointRateLimitInput = {};
  let changed = false;

  if (pub.value !== prevPub) {
    // null clears override when previously set; number sets/changes.
    body.public_rpm = pub.value;
    changed = true;
  }
  if (svc.value !== prevSvc) {
    body.service_rpm = svc.value;
    changed = true;
  }

  if (!changed) {
    return { ok: true, body: {}, noChange: true };
  }

  // If user cleared both (or only changed fields result in full clear),
  // send explicit nulls for fields that should clear.
  if (body.public_rpm === undefined && body.service_rpm === undefined) {
    return { ok: true, body: {}, noChange: true };
  }

  return { ok: true, body, noChange: false };
}

export function rateLimitStatusLabel(item: DataEndpointRateLimit | null): {
  label: string;
  tone: "default" | "custom";
} {
  if (!item || item.using_defaults) {
    return { label: "Using defaults", tone: "default" };
  }
  const parts: string[] = [];
  if (item.public_overridden) parts.push(`public ${item.effective_public_rpm}`);
  if (item.service_overridden)
    parts.push(`service ${item.effective_service_rpm}`);
  return {
    label: parts.length ? `Custom · ${parts.join(" · ")}` : "Custom",
    tone: "custom",
  };
}

export function formValuesFromRateLimit(item: DataEndpointRateLimit | null): {
  publicRPM: string;
  serviceRPM: string;
} {
  return {
    publicRPM:
      item?.public_overridden && item.public_rpm != null
        ? String(item.public_rpm)
        : "",
    serviceRPM:
      item?.service_overridden && item.service_rpm != null
        ? String(item.service_rpm)
        : "",
  };
}

export function summarizeOverrides(
  list: DataEndpointRateLimit[],
): { table: string; summary: string; updated_at?: string }[] {
  return list
    .filter((x) => !x.using_defaults)
    .map((x) => {
      const bits: string[] = [];
      if (x.public_overridden)
        bits.push(`public ${x.effective_public_rpm}/min`);
      if (x.service_overridden)
        bits.push(`service ${x.effective_service_rpm}/min`);
      return {
        table: x.table_name,
        summary: bits.join(" · ") || "custom",
        updated_at: x.updated_at,
      };
    });
}
